/**
 * Nextrip¹ Premium Motion Controller
 * CSS + vanilla JS only. Progressive enhancement. Theme Editor aware.
 */
(function () {
  'use strict';

  var ROOT = document.documentElement;
  var CONFIG = window.NextripMotionConfig || {};
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var TOUCH = window.matchMedia('(hover: none), (pointer: coarse)').matches;
  var LOW_POWER = false;

  try {
    if (navigator.getBattery) {
      navigator.getBattery().then(function (b) {
        LOW_POWER = !!(b && b.charging === false && b.level < 0.2);
        if (LOW_POWER) disableHeavy();
      }).catch(function () {});
    }
  } catch (e) {}

  var state = {
    enabled: CONFIG.enableAnimations !== false,
    intensity: CONFIG.intensity || 'standard',
    speed: CONFIG.speed || 'standard',
    ambient: CONFIG.enableAmbient !== false,
    parallax: CONFIG.enableParallax !== false,
    countUp: CONFIG.enableCountUp !== false,
    progress: CONFIG.enableScrollProgress !== false,
    hover: CONFIG.enableHover !== false,
    observers: new WeakMap(),
    sectionInits: new WeakSet(),
    revealed: new WeakSet(),
    rafScroll: 0,
    breatheTimer: 0
  };

  function cfgBool(v, fallback) {
    if (typeof v === 'boolean') return v;
    if (v === 'false' || v === false) return false;
    if (v === 'true' || v === true) return true;
    return fallback;
  }

  function applyRootFlags() {
    state.enabled = cfgBool(CONFIG.enableAnimations, true) && !REDUCED;
    state.intensity = CONFIG.intensity || 'standard';
    state.speed = CONFIG.speed || 'standard';
    state.ambient = cfgBool(CONFIG.enableAmbient, true) && !REDUCED;
    state.parallax = cfgBool(CONFIG.enableParallax, true) && !REDUCED && !TOUCH && !LOW_POWER;
    state.countUp = cfgBool(CONFIG.enableCountUp, true) && !REDUCED;
    state.progress = cfgBool(CONFIG.enableScrollProgress, true) && !REDUCED;
    state.hover = cfgBool(CONFIG.enableHover, true) && !REDUCED && !TOUCH && !LOW_POWER;

    ROOT.setAttribute('data-motion-intensity', state.intensity);
    ROOT.setAttribute('data-motion-speed', state.speed === 'standard' ? 'standard' : state.speed);
    ROOT.setAttribute('data-motion-ambient', state.ambient ? 'true' : 'false');
    ROOT.setAttribute('data-motion-parallax', state.parallax ? 'true' : 'false');
    ROOT.setAttribute('data-motion-progress', state.progress ? 'true' : 'false');
    ROOT.setAttribute('data-motion-hover', state.hover ? 'true' : 'false');

    if (state.enabled) {
      ROOT.classList.add('n1-motion-enabled');
      ROOT.classList.remove('n1-motion-disabled');
    } else {
      ROOT.classList.remove('n1-motion-enabled');
      ROOT.classList.add('n1-motion-disabled');
      revealAllImmediate(document);
    }
  }

  function disableHeavy() {
    state.parallax = false;
    state.ambient = false;
    state.hover = false;
    ROOT.setAttribute('data-motion-parallax', 'false');
    ROOT.setAttribute('data-motion-ambient', 'false');
    ROOT.setAttribute('data-motion-hover', 'false');
  }

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  function markRevealed(el) {
    if (!el || state.revealed.has(el)) return;
    state.revealed.add(el);
    el.classList.add('is-revealed', 'is-visible');
    if (el.hasAttribute('data-count-up') && state.countUp) runCountUp(el);
    if (el.classList.contains('n1-btn') || el.matches('.n1-btn')) maybePulseOnce(el);
    if (el.classList.contains('n1-culture-card__tag')) {
      el.classList.add('is-bounce-once');
    }
    if (el.classList.contains('n1-founder__video')) {
      el.classList.add('is-pulse-once');
    }
  }

  function revealAllImmediate(root) {
    (root || document).querySelectorAll('[data-reveal], .n1-reveal, [data-stagger]').forEach(function (el) {
      el.classList.add('is-revealed', 'is-visible');
    });
  }

  function getObserver(rootEl) {
    if (!state.enabled) return null;
    if (state.observers.has(rootEl)) return state.observers.get(rootEl);

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        markRevealed(entry.target);
        io.unobserve(entry.target);
      });
    }, {
      threshold: 0.18,
      rootMargin: '0px 0px -8% 0px'
    });

    state.observers.set(rootEl, io);
    return io;
  }

  function observeRevealables(scope) {
    var root = scope || document;
    if (!state.enabled) {
      revealAllImmediate(root);
      return;
    }

    var io = getObserver(document);
    if (!io) return;

    var nodes = root.querySelectorAll('[data-reveal], .n1-reveal, [data-stagger]');
    nodes.forEach(function (el) {
      if (el.classList.contains('n1-motion-host') && !el.hasAttribute('data-reveal') && !el.hasAttribute('data-stagger')) {
        el.classList.add('is-revealed', 'is-visible');
        return;
      }
      if (state.revealed.has(el) || el.classList.contains('is-revealed')) return;
      /* Hero intros are handled by dedicated CSS/JS */
      if (el.closest('[data-n1-hero], [data-n1-fs-hero]')) return;
      io.observe(el);
    });
  }

  function initSection(sectionEl, replay) {
    if (!sectionEl) return;
    if (!replay && state.sectionInits.has(sectionEl)) return;
    state.sectionInits.add(sectionEl);

    if (replay) {
      sectionEl.querySelectorAll('[data-reveal], .n1-reveal, [data-stagger]').forEach(function (el) {
        state.revealed.delete(el);
        el.classList.remove('is-revealed', 'is-visible');
      });
    }

    observeRevealables(sectionEl);

    var hero = sectionEl.matches('[data-n1-hero]') ? sectionEl : sectionEl.querySelector('[data-n1-hero]');
    if (hero) initHeroExtras(hero, replay);

    var faq = sectionEl.querySelector('[data-n1-faq]');
    if (faq) initFaqMotion(faq);

    var steps = sectionEl.querySelector('.n1-steps');
    if (steps) {
      steps.setAttribute('data-stagger', '');
      if (!steps.hasAttribute('data-reveal')) steps.setAttribute('data-reveal', 'up');
    }

    var fitCta = sectionEl.querySelector('.n1-fit__form .n1-btn--primary');
    if (fitCta) fitCta.setAttribute('data-pulse-once', '');

    enhanceImages(sectionEl);
  }

  function enhanceImages(scope) {
    scope.querySelectorAll('.n1-founder__photo, .n1-page-hero__bg, .n1-team-card__photo, .n1-culture-card img').forEach(function (img) {
      var wrap = img.closest('.n1-founder__media') || img;
      if (wrap && !wrap.hasAttribute('data-reveal') && wrap.classList.contains('n1-founder__media')) {
        wrap.setAttribute('data-reveal', 'clip');
      }
    });
  }

  function maybePulseOnce(el) {
    if (!el.hasAttribute('data-pulse-once')) return;
    if (el.dataset.pulsed === 'true') return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        el.dataset.pulsed = 'true';
        el.classList.add('is-pulse-once');
        io.unobserve(el);
        window.setTimeout(function () { el.classList.remove('is-pulse-once'); }, 1400);
      });
    }, { threshold: 0.6 });
    io.observe(el);
  }

  /* ---------------- Count-up ---------------- */
  function parseStat(text) {
    var raw = (text || '').trim();
    if (!raw) return null;
    if (/24\s*\/\s*7/i.test(raw)) return { type: 'fade' };
    if (/\d+\s*[–—-]\s*\d+%/.test(raw)) {
      var range = raw.match(/(\d+)\s*[–—-]\s*(\d+)(%?)/);
      if (!range) return { type: 'fade' };
      return { type: 'range', a: parseInt(range[1], 10), b: parseInt(range[2], 10), suffix: range[3] || '%', original: raw };
    }
    var m = raw.match(/^([^\d]*)(\d+)([+]?)\s*(.*)$/);
    if (!m) return { type: 'fade' };
    return {
      type: 'number',
      prefix: m[1] || '',
      value: parseInt(m[2], 10),
      plus: m[3] || '',
      suffix: (m[4] || '').trim(),
      original: raw
    };
  }

  function runCountUp(el) {
    if (el.dataset.counted === 'true') return;
    el.dataset.counted = 'true';
    var parsed = parseStat(el.getAttribute('data-count-up') || el.textContent);
    if (!parsed || parsed.type === 'fade') return;

    var duration = 900 * (state.speed === 'slow' ? 1.2 : state.speed === 'fast' ? 0.85 : 1);
    var start = performance.now();

    if (parsed.type === 'range') {
      /* Fade endpoints carefully — show original string after brief numeric ease of end value */
      el.textContent = parsed.a + '–' + parsed.a + (parsed.suffix || '%');
      function frameRange(now) {
        var t = Math.min(1, (now - start) / duration);
        var eased = 1 - Math.pow(1 - t, 3);
        var current = Math.round(parsed.a + (parsed.b - parsed.a) * eased);
        el.textContent = parsed.a + '–' + current + (parsed.suffix || '%');
        if (t < 1) requestAnimationFrame(frameRange);
        else el.textContent = parsed.original;
      }
      requestAnimationFrame(frameRange);
      return;
    }

    function frame(now) {
      var t = Math.min(1, (now - start) / duration);
      var eased = 1 - Math.pow(1 - t, 3);
      var current = Math.round(parsed.value * eased);
      el.textContent = parsed.prefix + current + parsed.plus + (parsed.suffix ? ' ' + parsed.suffix : '');
      if (t < 1) requestAnimationFrame(frame);
      else el.textContent = parsed.original;
    }
    requestAnimationFrame(frame);
  }

  function prepareCountUps(scope) {
    (scope || document).querySelectorAll('.n1-hero__stat-value, [data-count-up]').forEach(function (el) {
      if (!el.hasAttribute('data-count-up')) {
        el.setAttribute('data-count-up', el.textContent.trim());
      }
    });
  }

  /* ---------------- Scroll progress ---------------- */
  function ensureProgressBar() {
    if (!state.progress) return null;
    var bar = document.querySelector('.n1-scroll-progress');
    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'n1-scroll-progress';
      bar.setAttribute('aria-hidden', 'true');
      document.body.appendChild(bar);
    }
    return bar;
  }

  function onScroll() {
    if (state.rafScroll) return;
    state.rafScroll = requestAnimationFrame(function () {
      state.rafScroll = 0;
      if (document.hidden) return;

      var scrollTop = window.scrollY || ROOT.scrollTop || 0;
      var docH = Math.max(ROOT.scrollHeight, document.body.scrollHeight) - window.innerHeight;
      var p = docH > 0 ? Math.min(1, Math.max(0, scrollTop / docH)) : 0;

      if (state.progress) {
        var bar = ensureProgressBar();
        if (bar) bar.style.transform = 'scaleX(' + p + ')';
      }

      document.querySelectorAll('.n1-hero__header, .n1-page-header').forEach(function (header) {
        header.classList.toggle('is-scrolled', scrollTop > 40);
      });

      if (state.parallax) updateParallax(scrollTop);

      /* WhatsApp float is owned by nextrip1-whatsapp.js */
    });
  }

  function updateParallax(scrollTop) {
    document.querySelectorAll('[data-parallax]').forEach(function (el) {
      var speed = parseFloat(el.getAttribute('data-parallax')) || 0.12;
      var rect = el.getBoundingClientRect();
      var center = rect.top + rect.height / 2 - window.innerHeight / 2;
      var y = Math.max(-18, Math.min(18, -center * speed * 0.05));
      el.style.setProperty('--parallax-y', y.toFixed(2) + 'px');
    });
  }

  function isHeaderControl(el) {
    return !!(el && el.closest && el.closest('.n1-hero__header, .n1-page-header, [data-n1-site-header], .n1-hero__drawer, .n1-page-header__drawer'));
  }

  /* ---------------- Magnetic hover ---------------- */
  function initMagnetic() {
    if (!state.hover) return;
    document.querySelectorAll('.n1-btn--primary, [data-magnetic]').forEach(function (el) {
      if (isHeaderControl(el)) return;
      if (
        el.classList.contains('n1-hero__member-btn') ||
        el.classList.contains('n1-page-header__cta') ||
        el.classList.contains('n1-btn--become-member')
      ) return;
      if (el.dataset.magneticInit === 'true') return;
      el.dataset.magneticInit = 'true';
      el.setAttribute('data-magnetic', '');
      el.addEventListener('pointermove', function (e) {
        if (TOUCH || !state.hover) return;
        var r = el.getBoundingClientRect();
        var x = e.clientX - (r.left + r.width / 2);
        var y = e.clientY - (r.top + r.height / 2);
        var max = el.classList.contains('n1-card') || el.classList.contains('n1-service-card') ? 2 : 4;
        var mx = Math.max(-max, Math.min(max, x * 0.12));
        var my = Math.max(-max, Math.min(max, y * 0.12));
        el.style.setProperty('--mx', mx + 'px');
        el.style.setProperty('--my', my + 'px');
      });
      el.addEventListener('pointerleave', function () {
        el.style.setProperty('--mx', '0px');
        el.style.setProperty('--my', '0px');
      });
    });

    document.querySelectorAll('.n1-service-card, [data-hover-lift]').forEach(function (el) {
      if (isHeaderControl(el)) return;
      if (el.dataset.cardMag === 'true') return;
      el.dataset.cardMag = 'true';
      el.addEventListener('pointermove', function (e) {
        if (TOUCH || !state.hover) return;
        var r = el.getBoundingClientRect();
        var x = e.clientX - (r.left + r.width / 2);
        var y = e.clientY - (r.top + r.height / 2);
        el.style.transform = 'translate3d(' + (x * 0.02).toFixed(2) + 'px,' + (-4 + y * 0.02).toFixed(2) + 'px,0)';
      });
      el.addEventListener('pointerleave', function () {
        el.style.transform = '';
      });
    });
  }

  /* ---------------- Hero extras ---------------- */
  function initHeroExtras(hero, replay) {
    if (!hero) return;
    var plane = hero.querySelector('.n1-hero__plane');
    if (plane && state.parallax) plane.setAttribute('data-parallax', '0.15');

    prepareCountUps(hero);
    if (state.countUp && (hero.classList.contains('n1-hero--ready') || replay)) {
      window.setTimeout(function () {
        hero.querySelectorAll('[data-count-up]').forEach(runCountUp);
      }, 1500);
    } else if (state.countUp) {
      var mo = new MutationObserver(function () {
        if (hero.classList.contains('n1-hero--ready')) {
          mo.disconnect();
          window.setTimeout(function () {
            hero.querySelectorAll('[data-count-up]').forEach(runCountUp);
          }, 1500);
        }
      });
      mo.observe(hero, { attributes: true, attributeFilter: ['class'] });
    }
  }

  /* ---------------- FAQ ---------------- */
  function initFaqMotion(faq) {
    if (!faq || faq.dataset.motionFaq === 'true') return;
    faq.dataset.motionFaq = 'true';
    faq.querySelectorAll('[data-n1-faq-item]').forEach(function (item) {
      var answer = item.querySelector('.n1-faq__answer');
      if (!answer) return;
      /* Keep hidden attribute for a11y; CSS grid handles visual open state */
      var btn = item.querySelector('[data-n1-faq-toggle]');
      if (btn && !btn.hasAttribute('aria-controls')) {
        var id = 'faq-ans-' + Math.random().toString(36).slice(2, 9);
        answer.id = answer.id || id;
        btn.setAttribute('aria-controls', answer.id);
      }
    });
  }

  /* ---------------- WhatsApp (moved to nextrip1-whatsapp.js) ---------------- */
  function showWhatsApp() {}
  function initWhatsApp() {}

  /* ---------------- Visibility pause ---------------- */
  function onVisibility() {
    if (document.hidden) {
      ROOT.setAttribute('data-motion-ambient', 'false');
    } else if (state.ambient) {
      ROOT.setAttribute('data-motion-ambient', 'true');
    }
  }

  /* ---------------- Auto-wire common section patterns ---------------- */
  function autoWireSections() {
    var sections = document.querySelectorAll('.n1-section, .n1-fs-section, .n1-fs-hero, .n1-footer, .n1-page-header, .n1-page-hero, [data-n1-hero], [data-n1-fs-hero]');
    /* Avoid cycling left/right — horizontal reveals widen mobile scrollWidth (esp. RTL) */
    var dirs = ['up', 'up', 'scale', 'clip', 'up'];
    var idx = 0;
    var mobile = window.matchMedia('(max-width: 749px)').matches;

    function safeDir(raw) {
      if (!raw) return 'up';
      if (mobile && (raw === 'left' || raw === 'right')) return 'up';
      return raw;
    }

    sections.forEach(function (section) {
      if (section.matches('[data-n1-hero]')) return;
      if (!section.hasAttribute('data-motion-section')) {
        section.setAttribute('data-motion-section', dirs[idx % dirs.length]);
        idx += 1;
      }
      var dir = safeDir(section.getAttribute('data-motion-section') || 'up');

      var host = section.querySelector('.n1-container, .n1-container--wide, .n1-final-cta__inner, .n1-fs-wrap, .n1-fs-wrap--wide, .n1-fs-wrap--narrow, .n1-motion-host');
      if (host) {
        host.classList.add('n1-motion-host');
        if (host.classList.contains('n1-reveal') || host.classList.contains('n1-motion-host')) {
          host.classList.add('is-revealed', 'is-visible');
        }
      }

      /* Founder hero intro is owned by nextrip1-founder-page.js */
      if (section.matches('[data-n1-fs-hero], .n1-fs-hero') || section.querySelector('[data-n1-fs-hero]')) {
        initSection(section, false);
        return;
      }

      section.querySelectorAll('.n1-eyebrow, .n1-fs-eyebrow').forEach(function (el) {
        if (!el.hasAttribute('data-reveal')) el.setAttribute('data-reveal', 'up');
      });

      section.querySelectorAll('.n1-heading, .n1-fs-heading, .n1-page-hero .n1-heading').forEach(function (el) {
        if (!el.hasAttribute('data-reveal')) el.setAttribute('data-reveal', dir === 'clip' ? 'clip' : dir);
      });

      section.querySelectorAll('.n1-lead, .n1-disclaimer, .n1-note, .n1-services__footnote, .n1-compare__tagline').forEach(function (el) {
        if (!el.hasAttribute('data-reveal')) el.setAttribute('data-reveal', 'up');
      });

      var grids = section.querySelectorAll(
        '.n1-services__grid, .n1-benefits__grid, .n1-team__grid, .n1-testimonials__grid, .n1-intro__main, .n1-steps, .n1-fit__lists, .n1-compare, .n1-footer__grid'
      );
      grids.forEach(function (grid) {
        if (!grid.hasAttribute('data-stagger')) grid.setAttribute('data-stagger', '');
        if (!grid.hasAttribute('data-reveal')) grid.setAttribute('data-reveal', dir === 'left' || dir === 'right' ? 'up' : dir);
      });

      /* Carousel tracks must never get horizontal entrance transforms */
      section.querySelectorAll('.n1-culture-carousel__track, .n1-fb__gallery').forEach(function (track) {
        track.setAttribute('data-reveal', 'up');
      });

      section.querySelectorAll('.n1-service-card, .n1-benefit, .n1-team-card, .n1-culture-card').forEach(function (card) {
        if (!card.hasAttribute('data-hover-lift')) card.setAttribute('data-hover-lift', '');
      });

      section.querySelectorAll('.n1-btn-row, .n1-faq__cta, .n1-fit__form-wrap, .n1-founder__quote, .n1-founder__content > .n1-btn').forEach(function (el) {
        if (!el.hasAttribute('data-reveal')) el.setAttribute('data-reveal', 'up');
      });

      var media = section.querySelector('.n1-founder__media');
      if (media && !media.hasAttribute('data-reveal')) media.setAttribute('data-reveal', mobile ? 'up' : 'founder');

      var compareCols = section.querySelectorAll('.n1-compare__col');
      if (compareCols.length >= 2) {
        if (!compareCols[0].hasAttribute('data-reveal')) compareCols[0].setAttribute('data-reveal', mobile ? 'up' : 'left');
        if (!compareCols[1].hasAttribute('data-reveal')) compareCols[1].setAttribute('data-reveal', mobile ? 'up' : 'right');
      }

      var fitLists = section.querySelectorAll('.n1-fit__list');
      fitLists.forEach(function (list, i) {
        if (!list.hasAttribute('data-reveal')) list.setAttribute('data-reveal', mobile ? 'up' : (i % 2 ? 'right' : 'left'));
        var ul = list.querySelector('ul');
        if (ul && !ul.hasAttribute('data-stagger')) ul.setAttribute('data-stagger', '');
      });

      if (mobile) {
        section.querySelectorAll('[data-reveal="left"], [data-reveal="right"], [data-reveal="founder"]').forEach(function (el) {
          el.setAttribute('data-reveal', 'up');
        });
      }

      section.querySelectorAll('.n1-footer__brand, .n1-footer__col, .n1-lux-footer__col, .n1-lux-footer__cta').forEach(function (col) {
        if (!col.hasAttribute('data-reveal')) col.setAttribute('data-reveal', 'up');
      });
      var copyright = section.querySelector('.n1-footer__bottom, .n1-lux-footer__bottom');
      if (copyright && !copyright.hasAttribute('data-reveal')) copyright.setAttribute('data-reveal', 'fade');
      var luxGrid = section.querySelector('.n1-lux-footer__grid');
      if (luxGrid && !luxGrid.hasAttribute('data-stagger')) luxGrid.setAttribute('data-stagger', '');

      initSection(section, false);
    });
  }

  function lockHorizontalViewport() {
    try {
      var root = document.documentElement;
      var x = window.scrollX || window.pageXOffset || 0;
      if (Math.abs(x) > 0) {
        window.scrollTo(0, window.scrollY || window.pageYOffset || 0);
      }
      if (root) root.scrollLeft = 0;
      if (document.body) document.body.scrollLeft = 0;
    } catch (err) {}
  }

  function boot() {
    window.__N1_MOTION_READY = true;
    applyRootFlags();
    autoWireSections();
    prepareCountUps(document);
    observeRevealables(document);
    initMagnetic();
    initWhatsApp();
    ensureProgressBar();
    lockHorizontalViewport();

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', debounce(function () {
      onScroll();
      lockHorizontalViewport();
    }, 150), { passive: true });
    document.addEventListener('visibilitychange', onVisibility);
    onScroll();

    document.querySelectorAll('[data-pulse-once]').forEach(maybePulseOnce);

    document.querySelectorAll('.n1-culture-card__tag').forEach(function (tag) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          tag.classList.add('is-bounce-once');
          io.unobserve(tag);
        });
      }, { threshold: 0.5 });
      io.observe(tag);
    });
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(null, args); }, wait);
    };
  }

  /* Theme Editor */
  document.addEventListener('shopify:section:load', function (e) {
    applyRootFlags();
    var section = e.target;
    if (!section) return;
    /* clear prior init marker by cloning sectionInits via replay */
    state.sectionInits.delete(section);
    autoWireOne(section, true);
    initMagnetic();
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var section = e.target;
    if (!section) return;
    state.sectionInits.delete(section);
    var io = state.observers.get(document);
    if (io) {
      section.querySelectorAll('[data-reveal], .n1-reveal, [data-stagger]').forEach(function (el) {
        try { io.unobserve(el); } catch (err) {}
      });
    }
  });

  document.addEventListener('shopify:block:select', function (e) {
    var block = e.target;
    if (!block) return;
    markRevealed(block);
    block.scrollIntoView({ behavior: REDUCED ? 'auto' : 'smooth', block: 'nearest' });
  });

  function autoWireOne(section, replay) {
    var dirs = ['up', 'up', 'scale', 'clip', 'up'];
    if (!section.hasAttribute('data-motion-section') && !section.querySelector('[data-n1-hero]')) {
      section.setAttribute('data-motion-section', dirs[0]);
    }
    var host = section.querySelector('.n1-container, .n1-container--wide');
    if (host) {
      host.classList.add('n1-motion-host', 'is-revealed', 'is-visible');
    }
    section.querySelectorAll('.n1-eyebrow').forEach(function (el) {
      el.setAttribute('data-reveal', 'up');
      if (replay) { el.classList.remove('is-revealed', 'is-visible'); state.revealed.delete(el); }
    });
    section.querySelectorAll('.n1-heading, .n1-lead, .n1-card, .n1-benefit, .n1-step, .n1-testimonial, .n1-team-card').forEach(function (el) {
      if (!el.hasAttribute('data-reveal')) el.setAttribute('data-reveal', section.getAttribute('data-motion-section') || 'up');
      if (replay) { el.classList.remove('is-revealed', 'is-visible'); state.revealed.delete(el); }
    });
    initSection(section, !!replay);
    observeRevealables(section);
  }

  ready(boot);

  window.NextripMotion = {
    refresh: function (el) { autoWireOne(el || document.body, true); },
    revealAll: revealAllImmediate,
    config: state
  };
})();
